/**
 * Configuration Verification Utility
 * 
 * This utility helps verify that all required environment variables
 * are properly set for production deployments.
 * 
 * Usage in React component:
 * import { verifyConfig } from './utils/configVerification';
 * 
 * In useEffect:
 * if (process.env.NODE_ENV === 'production') {
 *   const verification = verifyConfig();
 *   console.log('Config Verification:', verification);
 * }
 */

import { API_URL, SOCKET_URL } from '../config/apiConfig';

/**
 * Verify all required environment variables are set
 * @returns {Object} Verification results
 */
export const verifyConfig = () => {
  const verification = {
    isProduction: process.env.NODE_ENV === 'production',
    hostname: window.location.hostname,
    timestamp: new Date().toISOString(),
    checks: {},
    warnings: [],
    errors: []
  };

  // Check Google Maps API Key
  const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  verification.checks.googleMapsApiKey = {
    isSet: !!googleMapsKey,
    value: googleMapsKey ? `${googleMapsKey.substring(0, 10)}...` : 'NOT SET',
    status: googleMapsKey ? 'OK' : 'MISSING'
  };
  if (!googleMapsKey && verification.isProduction) {
    verification.errors.push('REACT_APP_GOOGLE_MAPS_API_KEY is not set in production');
  }

  // Check API URL
  verification.checks.apiUrl = {
    value: API_URL,
    status: API_URL ? 'OK' : 'MISSING'
  };
  if (!API_URL) {
    verification.warnings.push('API_URL is not configured');
  }

  // Check Socket URL
  verification.checks.socketUrl = {
    value: SOCKET_URL,
    status: SOCKET_URL ? 'OK' : 'MISSING',
    isHttps: SOCKET_URL ? SOCKET_URL.startsWith('https://') : false
  };
  if (!SOCKET_URL) {
    verification.errors.push('SOCKET_URL is not configured');
  } else if (!SOCKET_URL.startsWith('https://') && verification.isProduction) {
    verification.warnings.push('SOCKET_URL should use HTTPS in production');
  }

  // Check Razorpay Key
  const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
  verification.checks.razorpayKey = {
    isSet: !!razorpayKey,
    value: razorpayKey ? `${razorpayKey.substring(0, 10)}...` : 'NOT SET',
    status: razorpayKey ? 'OK' : 'MISSING'
  };
  if (!razorpayKey && verification.isProduction) {
    verification.warnings.push('REACT_APP_RAZORPAY_KEY_ID is not set (payments may fail)');
  }

  // Check Base URL
  const baseUrl = process.env.REACT_APP_BASE_URL;
  verification.checks.baseUrl = {
    value: baseUrl || 'Not explicitly set (defaults to API_URL)',
    status: baseUrl ? 'OK' : 'DEFAULT'
  };

  // Network connectivity checks (async, returned separately)
  verification.networkChecks = {
    apiConnectivity: 'pending',
    socketConnectivity: 'pending'
  };

  return verification;
};

/**
 * Test connectivity to backend services
 * @returns {Promise<Object>} Connectivity test results
 */
export const testBackendConnectivity = async () => {
  const results = {
    api: { status: 'pending', latency: null, error: null },
    socket: { status: 'pending', canConnect: false, error: null }
  };

  // Test API connectivity
  try {
    const startTime = performance.now();
    const response = await fetch(`${API_URL}/health`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    });
    const latency = performance.now() - startTime;
    results.api.status = response.ok ? 'success' : 'http-error';
    results.api.latency = latency;
    results.api.statusCode = response.status;
  } catch (error) {
    results.api.status = 'error';
    results.api.error = error.message;
  }

  // Test Socket connectivity (just verify the URL is reachable)
  try {
    const response = await fetch(SOCKET_URL, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'include'
    });
    results.socket.status = response.ok ? 'reachable' : 'unreachable';
    results.socket.canConnect = true;
  } catch (error) {
    results.socket.status = 'unreachable';
    results.socket.error = error.message;
    results.socket.canConnect = false;
  }

  return results;
};

/**
 * Log configuration details for debugging
 */
export const logConfigDetails = () => {
  const verification = verifyConfig();
  
  console.group('🔧 CabZigo Configuration Details');
  console.log('Environment:', verification.isProduction ? 'PRODUCTION' : 'DEVELOPMENT');
  console.log('Current Hostname:', verification.hostname);
  console.log('Current URL:', window.location.href);
  console.log('');
  
  console.group('✅ Configuration Status');
  Object.entries(verification.checks).forEach(([key, check]) => {
    const icon = check.status === 'OK' ? '✓' : check.status === 'DEFAULT' ? '○' : '✗';
    console.log(`${icon} ${key}:`, check);
  });
  console.groupEnd();
  
  if (verification.errors.length > 0) {
    console.group('❌ Errors');
    verification.errors.forEach(error => console.error(error));
    console.groupEnd();
  }
  
  if (verification.warnings.length > 0) {
    console.group('⚠️ Warnings');
    verification.warnings.forEach(warning => console.warn(warning));
    console.groupEnd();
  }
  
  console.log('');
  console.log('Full Verification Object:', verification);
  console.groupEnd();
};

/**
 * Initialize configuration check on app startup
 * Call this in App.js useEffect
 */
export const initializeConfigCheck = () => {
  if (typeof window !== 'undefined') {
    // Log config details in development
    if (process.env.NODE_ENV === 'development') {
      logConfigDetails();
    } else {
      // In production, only log if there are errors/warnings
      const verification = verifyConfig();
      if (verification.errors.length > 0 || verification.warnings.length > 0) {
        logConfigDetails();
      }
    }
  }
};
